import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy } from 'lucide-react';
import { AuthContext } from '../App';
import { useEventConfig } from '../hooks/useEventConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('pt-BR', { minimumFractionDigits: v % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
};

const firstName = (full) => (full || '').trim().split(/\s+/)[0] || '';

const buildDuplaTitle = (athletes) => {
  const names = athletes.map((a) => firstName(a.full_name)).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return names.join(' + ');
};

const RankingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const { config } = useEventConfig();
  const [duplas, setDuplas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Geral');

  useEffect(() => {
    if (!user || !token) {
      navigate('/inscricao');
      return;
    }
    fetchRanking();
  }, [token]);

  const fetchRanking = async () => {
    try {
      const res = await axios.get(`${API}/ranking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDuplas(res.data.duplas || []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) navigate('/inscricao');
    } finally {
      setLoading(false);
    }
  };

  const filterTabs = ['Geral', ...config.categories];
  const filtered =
    filter === 'Geral' ? duplas : duplas.filter((d) => d.level === filter);
  const positioned = filtered.map((d, idx) => ({ ...d, displayPos: idx + 1 }));

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-xs uppercase tracking-wider">Voltar</span>
          </button>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="font-mono text-xs uppercase tracking-wider" data-testid="ranking-event-name">
              {config.event_name}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            Pontuação total = Km base + bônus
          </p>
          <h1
            data-testid="ranking-title"
            className="font-unbounded font-black text-4xl md:text-5xl tracking-tight uppercase leading-none mb-8"
          >
            Ranking das Duplas
          </h1>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {filterTabs.map((cat) => {
              const isActive = filter === cat;
              return (
                <button
                  key={cat}
                  data-testid={`filter-${cat}`}
                  onClick={() => setFilter(cat)}
                  className={`px-5 py-2 rounded-full border transition-all font-mono text-xs flex items-center gap-2 ${
                    isActive
                      ? 'bg-white/10 border-white/20 text-white'
                      : 'bg-transparent border-white/15 text-muted-foreground hover:text-white hover:border-white/25'
                  }`}
                >
                  {cat !== 'Geral' && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Table card */}
          <div className="bg-card/40 border border-white/10 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="hidden md:grid grid-cols-[90px_1fr_200px_140px_120px_140px] gap-4 px-6 py-4 bg-black/30 border-b border-white/10">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pos</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Dupla</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Categoria</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-right">Km Base</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-right">Bônus</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-right">Total</span>
            </div>

            {loading ? (
              <div className="p-10 text-center text-muted-foreground font-mono text-sm" data-testid="ranking-loading">
                Carregando ranking...
              </div>
            ) : positioned.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground font-mono text-sm" data-testid="ranking-empty">
                Nenhuma dupla nesta categoria ainda.
              </div>
            ) : (
              positioned.map((d) => {
                const duplaTitle = buildDuplaTitle(d.athletes) || d.dupla_name;
                return (
                  <div
                    key={`${d.dupla_name}-${d.level}`}
                    data-testid={`ranking-row-${d.displayPos}`}
                    className="grid grid-cols-1 md:grid-cols-[90px_1fr_200px_140px_120px_140px] gap-4 md:gap-4 px-4 md:px-6 py-5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    {/* Position */}
                    <div className="flex items-start md:items-center">
                      <div className="w-12 h-9 flex items-center justify-center font-unbounded font-bold text-base rounded-md bg-white/5 border border-white/15 text-white">
                        {d.displayPos}
                      </div>
                    </div>

                    {/* Dupla */}
                    <div className="min-w-0">
                      <p
                        className="font-unbounded font-bold text-base md:text-[17px] uppercase tracking-tight text-white"
                        data-testid={`dupla-name-${d.displayPos}`}
                      >
                        {duplaTitle}
                      </p>
                      <div className="mt-1.5 space-y-0.5">
                        {d.athletes.map((a) => (
                          <p key={a.id} className="text-xs text-muted-foreground">
                            {a.full_name}
                          </p>
                        ))}
                      </div>
                    </div>

                    {/* Categoria */}
                    <div className="flex items-start md:items-center">
                      <span className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 rounded-full text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {d.level}
                      </span>
                    </div>

                    {/* Km Base */}
                    <div className="flex flex-col md:items-end">
                      <span className="md:hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Km Base</span>
                      <span className="font-unbounded font-bold text-base md:text-[17px] text-white md:text-right">
                        {fmt(d.km_base)}
                      </span>
                      <div className="mt-1.5 space-y-0.5 md:text-right">
                        {d.athletes.map((a) => (
                          <p key={a.id} className="text-xs text-muted-foreground font-mono">
                            {fmt(a.km_base)}
                          </p>
                        ))}
                      </div>
                    </div>

                    {/* Bônus */}
                    <div className="flex flex-col md:items-end">
                      <span className="md:hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Bônus</span>
                      <span className="font-unbounded font-bold text-base md:text-[17px] text-white md:text-right">
                        +{fmt(d.km_bonus)}
                      </span>
                      <div className="mt-1.5 space-y-0.5 md:text-right">
                        {d.athletes.map((a) => (
                          <p key={a.id} className="text-xs text-muted-foreground font-mono">
                            +{fmt(a.km_bonus)}
                          </p>
                        ))}
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex items-start md:items-center md:justify-end">
                      <span className="md:hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Total</span>
                      <span className="font-unbounded font-black text-2xl md:text-[28px] text-white md:text-right leading-none">
                        {fmt(d.total)}
                        <span className="text-muted-foreground text-xs ml-1 font-normal align-top">km</span>
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RankingPage;
